import i18n from "i18next";
import { type FC, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useAcquirerDeletePhysicalToVirtualMutation } from "src/app/features/Acquirer/services/server/AcquirerApi";
import type { AcquirerEntity } from "src/app/features/Acquirer/types/AcquirerEntity";
import type { AcquirerToVirtualEntity } from "src/app/features/Acquirer/types/subTypes/AcquirerToVirtualEntity";
import { formatValidationErrors } from "../../../../core/errorHandling/validationErrorParser";
import { ResourceName, ResourceVariant } from "../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../core/router/types/routerTypes";
import { showSuccessNotification, showWarningNotification } from "../../../../core/setup/redux/state/notifications/notificationsState";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { Actions } from "../../../Table/types/types";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";

const getColumnsConfig = (t: Function): ExtendedColumnsType<AcquirerToVirtualEntity>[] => {
    return [
        {
            title: t("acquirerCreateForm.table.systemId"),
            key: "physicalAcquirerId",
            dataIndex: "physicalAcquirerId",
        },
        {
            title: t("acquirerCreateForm.table.name"),
            key: "physicalAcquirerName",
            dataIndex: "physicalAcquirerName",
        },
        {
            title: t("acquirerCreateForm.table.shortName"),
            key: "shortName",
            dataIndex: "shortName",
            editable: true,
        },
        {
            title: t("acquirerCreateForm.table.onUsBinRange"),
            key: "onUsBinRange",
            dataIndex: "onUsBinRange",
            editable: true,
        },
        {
            title: t("acquirerCreateForm.table.offUsBinRange"),
            key: "offUsBinRange",
            dataIndex: "offUsBinRange",
            editable: true,
        },
        {
            title: t("acquirerCreateForm.table.offUsPercentage"),
            dataIndex: "offUsPercentage",
            key: "offUsPercentage",
            editable: true,
        },
    ];
};

export interface VirtualAcquirersPhysicalAcquirersTableProps extends AntCustomFormField<AcquirerToVirtualEntity[]>, CommonFieldAttributes {}

export const VirtualAcquirersPhysicalAcquirersTable: FC<VirtualAcquirersPhysicalAcquirersTableProps> = ({
    value = [],
    onChange,
    readOnly,
}) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const resourceUrl = `${ResourceName.ACQUIRER}/${ResourceVariant.VIRTUAL}`;

    const onDataChanged = (data: AcquirerToVirtualEntity[]) => {
        if (onChange) {
            onChange(data);
        }
    };

    const onRowClick = (record: AcquirerEntity) => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${id}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    const addCallback = () => {
        navigate(`${BASE_PATH}/${resourceUrl}/${FORM_ACTION.EDIT}/${id}/${FORM_ACTION.CREATE}`);
    };

    const [deleteTableItem] = useAcquirerDeletePhysicalToVirtualMutation();

    const deleteCallback = (item: AcquirerToVirtualEntity) => {
        deleteTableItem({
            acquirerToVirtualId: item.id,
            virtualAcquirerId: id,
        })
            .unwrap()
            .then((payload) => {
                performDeleteSuccess(payload);
            })
            .catch((error) => {
                const errorMessage = formatValidationErrors(error.data, i18n.t);
                dispatch(
                    showWarningNotification({
                        text: errorMessage,
                    }),
                );
            });

        const performDeleteSuccess = (payload: AcquirerToVirtualEntity) => {
            if (payload) {
                notifyOkDelete(payload);
            }
            onDataChanged(value.filter((record) => record.id !== item.id));
        };
    };

    const notifyOkDelete = useCallback(
        (payload: AcquirerToVirtualEntity) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: "Acquirer Physical to Virtual",
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    return (
        <PropertyTable<AcquirerToVirtualEntity>
            getColumnsConfig={getColumnsConfig}
            name="virtualAcquirerPhysicalAcqiurer_table"
            propertyAsRowKey="id"
            tableData={value || []}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            onRow={(record: AcquirerToVirtualEntity) => {
                return {
                    onClick: () => onRowClick(record),
                };
            }}
            resourceUrl={resourceUrl}
            actions={[Actions.DELETE, Actions.ADD]}
            actionCallbacks={{ add: addCallback, delete: (item?: AcquirerToVirtualEntity) => item && deleteCallback(item) }}
        />
    );
};
